import { Suspense, useCallback, useEffect, useMemo } from 'react';
import useStepper from 'hooks/useStepper';
import type { StepType } from 'components/layout';
import { FormNavigation, StepperPaper } from 'components/layout';
import type { Resolver } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import AdAccountsForm from './AdAccountsForm';
import CampaignsForm from './CampaignsForm';
import AdSetsForm from './AdSetsForm';
import CreativesForm from './CreativesForm';
import MarketingProcessTabs from './MarketingProcessTabs';
import AdForm from './AdForm';
import useJobStatusCheck from 'hooks/useJobStatusCheck';
import type {
  CreateFacebookAdFromPacksParams,
  CreateFacebookAdResponse,
  CreatePartnershipAdsParams,
  CreateScalingEgAdsParams
} from '../api';
import { useCampaigns, useCreateFacebookAd, useCreatePartnershipAds, useCreateScalingEgAds } from '../api';
import queryKeys from '../api/queryKeys';
import { AdCreationProgress, AddPackButton, CircularProgress, ConfirmationDialog } from 'components/common';
import AddTestingPackButton from './AddTestingPackButton';
import CreateMixedPackButton from './CreateMixedPackButton';
import SplitInPacksButton from './SplitInPacksButton';
import { JobResultTable } from './FacebookFormComponents';
import { queryClient } from 'lib/react-query';
import { useEnqueueSnackbar, useLocationParams } from 'hooks';
import { useIsFetching } from '@tanstack/react-query';
import { PartnershipCreationMode, PartnershipIdentityToShow } from '../types';
import facebookValidator, { type FacebookFormData } from '../validation/facebookValidator';
import scalingEgValidator, { type ScalingEgFormData } from '../validation/scalingEgValidator';
import { buildCampaignFetchParams } from '../utils/campaignFetchParams';
import { SINGLE_AD_TYPE } from '../constants';
import { useMe } from 'features/login';
import { useGlobalSettings } from 'providers';
import AdSetBudgetCounter from './AdSetBudgetCounter';
import { APP_LIST, MarketingProcessCode } from '../../../constants';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { DEFAULT_TEST_CREATIVES_VIEW_STATE } from '../types/hypothesis';
import { Box } from '@mui/system';
import { buildFacebookAdSubmitContext } from '../utils/facebookAdSubmitContext';
import { buildCreateAdFromPacksPayload, buildCreatePartnershipAdsPayload, } from '../utils/facebookAdSubmitPayload';
import { buildCreateScalingEgAdsPayload } from '../utils/scalingEgAdSubmitPayload';
import { isScalingEgProcess } from '../utils/marketingProcess';
import { AdSetAdsCounter } from 'features/facebook';
import type { PartnershipAdSettingFormValue } from './PartnershipSection';

type FormData = FacebookFormData;

const facebookFormResolver: Resolver<FormData> = async (values, context, options) => {
  if (isScalingEgProcess(values.marketingProcess)) {
    const scalingEgValues: ScalingEgFormData = {
      ...values,
      marketingProcess: 'scaling_eg',
    };

    return scalingEgValidator.resolver(
      scalingEgValues,
      context,
      options as Parameters<typeof scalingEgValidator.resolver>[2],
    );
  }

  return facebookValidator.resolver(values, context, options);
};

const FacebookForm = () => {
  const { data: me } = useMe();
  const { selectedApp } = useGlobalSettings();
  const {
    marketingProcess: marketingProcessParam,
    app: appParam,
    adType: adTypeParam,
  } = useLocationParams();
  const { onShowErrorAlert } = useEnqueueSnackbar();
  const isHypothesisTestingParam =
    marketingProcessParam === MarketingProcessCode.HYPOTHESIS_TESTING;
  const resolvedAdType = isHypothesisTestingParam
    ? SINGLE_AD_TYPE
    : (adTypeParam ?? '');

  const getDefaultPartnershipAdSetting = useCallback((): PartnershipAdSettingFormValue => ({
    partnershipId: '',
    partnershipTag: '',
    partnershipPartnerPageId: '',
    partnershipAppPageId: '',
    partnershipPartnerInstagramId: null,
  }), []);

  const initialSteps = useMemo<StepType[]>(
    () => [
      { title: 'Creatives', component: CreativesForm },
      { title: 'Ad Accounts & Platform', component: AdAccountsForm },
      { title: 'Campaigns', component: CampaignsForm },
      { title: 'Ad Sets', component: AdSetsForm },
      { title: 'Ad', component: AdForm },
    ],
    [],
  );
  const { currentStep, resetSteps, prevStep, nextStep, StepComponent } =
    useStepper({ initialSteps, defaultStep: 0 });

  const adSettingsIsFetching = useIsFetching({
    queryKey: queryKeys.adSettings,
  });
  const partnershipAdSettingsIsFetching = useIsFetching({
    queryKey: queryKeys.partnershipAdSettings,
  });

  const getDefaultValues = useMemo(() => {
    const availableApps = APP_LIST.map(a => String(a.code));
    const matchedProjects = me?.apps.filter((myApp) => availableApps.includes(myApp));

    return {
      currentStep: 0,
      app: appParam ?? selectedApp ?? (matchedProjects ? matchedProjects[0] : undefined),
      platform: 'WEB',
      marketingProcess: marketingProcessParam,
      adType: resolvedAdType,
      adAccounts: [],
      selectedCampaigns: [],
      selectedCampaignsOnly: false,
      selectedCampaignsBudgets: {},
      selectedAdSets: [],
      selectedAdSetsBudgets: {},
      selectedAdSetsDuplicatesCount: {},
      selectedAdSetsStartTimes: {},
      selectedCreatives: [],
      packsWithCreatives: [
        {
          tag: '',
          adType: resolvedAdType,
          creatives: [],
          filterCreativeByName: [],
          visual_touch_ups: true,
        },
      ],
      activePacksWithCreatives: 0,
      selectedAdSettings: [{}],
      selectedConversionDomain: '',
      adLabelSuffix: '',
      callToActions: 'LEARN_MORE',
      hotjar: false,
      partnershipAdsEnabled: false,
      partnershipIsPartnerIdentityFirst: true,
      partnershipIdentityToShow: PartnershipIdentityToShow.BOTH,
      partnershipCreationMode: PartnershipCreationMode.PARTNERSHIP_ONLY,
      partnershipAdSettings: [getDefaultPartnershipAdSetting()],
      filterCampaignsByName: [],
      filterAdSetsByName: [],
      filterCreativesByName: [],
      selectedCreativesOnly: false,
      activePackTag: '',
      testCreativesViewState: DEFAULT_TEST_CREATIVES_VIEW_STATE,
    };
  }, [me, selectedApp, appParam, marketingProcessParam, resolvedAdType, getDefaultPartnershipAdSetting]);

  const formMethods = useForm<FormData>({
    resolver: facebookFormResolver,
    defaultValues: getDefaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });

  const {
    watch,
    setValue,
    trigger,
    reset: resetForm,
    handleSubmit,
    formState: { isValid, errors },
  } = formMethods;
  const selectedFormApp = watch('app');

  useEffect(() => {
    setValue('currentStep', currentStep, { shouldValidate: true });
  }, [currentStep, setValue]);

  const isAdStep = currentStep === initialSteps.length - 1;

  useEffect(() => {
    if (!isAdStep) {
      return;
    }

    if (adSettingsIsFetching || partnershipAdSettingsIsFetching) {
      return;
    }

    void trigger();
  }, [
    adSettingsIsFetching,
    isAdStep,
    partnershipAdSettingsIsFetching,
    trigger,
  ]);

  useEffect(() => {
    const stepTitle = initialSteps[currentStep]?.title;
    if (!stepTitle) {
      return;
    }

    datadogActions.trackDatadogAction(DATADOG_EVENT.facebookFormStepChange, {
      stepTitle,
      stepIndex: currentStep,
    });
  }, [currentStep, initialSteps]);

  const {
    data: jobStatusResponse,
    runJubStatusCheck,
    isLoading: isJobStatusChecking,
    isFinished: isJobStatusFinished,
    clearJobStatus,
  } = useJobStatusCheck();

  useEffect(() => {
    if (
      !selectedApp ||
      String(selectedFormApp ?? '') === String(selectedApp)
    ) {
      return;
    }

    resetForm({
      ...getDefaultValues,
      app: selectedApp,
      currentStep: 0,
    });
    resetSteps();
    clearJobStatus();
  }, [
    clearJobStatus,
    getDefaultValues,
    resetForm,
    resetSteps,
    selectedApp,
    selectedFormApp,
  ]);

  const createAdsMutationConfig = useMemo(
    () => ({
      onSuccess: (r: CreateFacebookAdResponse) => {
        datadogActions.trackDatadogAction(DATADOG_EVENT.createFacebookAdSuccess, {
          jobId: r.job_id,
        });
        runJubStatusCheck(r.job_id);
      },
      onError: (err: Error & { cause?: unknown }) => {
        datadogActions.trackDatadogError(err, { source: 'create_facebook_ad_error' });
        if (err.cause) {
          onShowErrorAlert(
            (err.cause as { details?: string })?.details ?? 'Error occur',
            { delayAlert: 3500 },
          );
          console.error(err.cause);
        }
      },
    }),
    [onShowErrorAlert, runJubStatusCheck],
  );

  const createFacebookAd = useCreateFacebookAd({
    config: createAdsMutationConfig,
  });

  const createPartnershipAds = useCreatePartnershipAds({
    config: createAdsMutationConfig,
  });

  const createScalingEgAds = useCreateScalingEgAds({
    config: createAdsMutationConfig,
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      try {
        if (!isValid) return;

        const submitContext = buildFacebookAdSubmitContext(queryClient);

        if (isScalingEgProcess(data.marketingProcess)) {
          const payload: CreateScalingEgAdsParams = buildCreateScalingEgAdsPayload(
            data,
            submitContext,
          );
          createScalingEgAds.mutate(payload);
        } else {
          const allPacksAreSingleAd = data.packsWithCreatives.every(
            (pack) => pack.adType === SINGLE_AD_TYPE,
          );
          const isPartnershipAdsFlow = data.partnershipAdsEnabled && allPacksAreSingleAd;

          if (isPartnershipAdsFlow) {
            const payload: CreatePartnershipAdsParams = buildCreatePartnershipAdsPayload(
              data,
              submitContext,
            );
            createPartnershipAds.mutate(payload);
          } else {
            const payload: CreateFacebookAdFromPacksParams = buildCreateAdFromPacksPayload(
              data,
              submitContext,
            );
            createFacebookAd.mutate(payload);
          }
        }

        datadogActions.trackDatadogAction(DATADOG_EVENT.facebookFormSubmitAttempt, {
          app: data.app,
          platform: data.platform,
          marketingProcess: data.marketingProcess,
          adAccountsCount: data.adAccounts?.length ?? 0,
          adSetsCount: data.selectedAdSets?.length ?? 0,
          creativePacksCount: data.packsWithCreatives?.length ?? 0,
          partnershipAdsEnabled: data.partnershipAdsEnabled,
        });
      } catch (error) {
        console.error('Error during Facebook ad creation:', error);
        datadogActions.trackDatadogError(error as Error, { source: 'facebook_form_submit_try_catch' });
        onShowErrorAlert('Error during Facebook ad creation', {
          delayAlert: 3500,
        });
      }
    },
    [isValid, onShowErrorAlert, createFacebookAd, createPartnershipAds, createScalingEgAds],
  );

  const adAccounts: string[] | undefined = formMethods.watch('adAccounts');
  const marketingProcess: string = formMethods.watch('marketingProcess');
  const isMarketingProcessHypothesisTesting = marketingProcess === 'hypothesis_testing';
  const isMarketingProcessScalingEg = isScalingEgProcess(marketingProcess);

  useCampaigns({
    params: buildCampaignFetchParams(marketingProcess, adAccounts ?? []),
    config: {
      enabled: !!adAccounts?.length && !!marketingProcess,
    },
  });

  const handleNextStep = useCallback(async () => {
    const valid = await trigger();
    if (!valid || createFacebookAd.isPending || createPartnershipAds.isPending || createScalingEgAds.isPending) return;

    if (currentStep === initialSteps.length - 1) {
      void handleSubmit(onSubmit)();
    } else {
      nextStep();
    }
  }, [
    nextStep,
    trigger,
    handleSubmit,
    onSubmit,
    initialSteps,
    currentStep,
    createFacebookAd.isPending,
    createPartnershipAds.isPending,
    createScalingEgAds.isPending,
  ]);

  const handlePrevStep = useCallback(() => {
    prevStep();
    void trigger();
  }, [prevStep, trigger]);

  const handleRestart = useCallback(() => {
    datadogActions.trackDatadogAction(DATADOG_EVENT.facebookFormRestart);
    resetForm();
    resetSteps();
    clearJobStatus();
  }, [resetForm, resetSteps, clearJobStatus]);

  const handleLaunchMoreAds = useCallback(() => {
    window.open('/facebook', '_blank', 'noopener');
  }, []);

  const isCreateAdsPending =
    createFacebookAd.isPending || createPartnershipAds.isPending || createScalingEgAds.isPending;
  const isCreateAdsSuccess =
    createFacebookAd.isSuccess || createPartnershipAds.isSuccess || createScalingEgAds.isSuccess;
  const isNavigationLocked = isCreateAdsPending || !!isJobStatusChecking;
  const isDisabledNext =
    !!adSettingsIsFetching ||
    !!partnershipAdSettingsIsFetching ||
    isNavigationLocked ||
    !isValid;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[FacebookForm]', {
        isValid,
        adSettingsIsFetching: !!adSettingsIsFetching,
        partnershipAdSettingsIsFetching: !!partnershipAdSettingsIsFetching,
        isNavigationLocked,
        validationErrors: errors,
      });
    }
  }, [
    isValid,
    errors,
    adSettingsIsFetching,
    partnershipAdSettingsIsFetching,
    isNavigationLocked,
  ]);

  return (
    <FormProvider {...formMethods}>
      <StepperPaper
        title={'Facebook Ads Automation'}
        steps={initialSteps}
        activeStep={currentStep}
        topTabs={<MarketingProcessTabs onProcessChange={resetSteps} />}
      >
      <Box sx={{flex: 1, display: 'flex', width: '100%', alignItems: 'start', flexDirection: 'column'}}>
        <Box sx={{ flex: 1, display: 'flex', width: '100%', alignItems: 'start', flexDirection: 'column', position: 'relative', overflow: 'auto' }}>
          <Box sx={{ flex: 1, display: 'flex', width: '100%', height: '100%', alignItems: 'start', justifyContent: 'start', flexDirection: 'column', position: 'absolute' }}>
            {isJobStatusChecking ? (
              <Box sx={{display: 'flex', width: '100%', height: '100%', flex: 1}}>
                <AdCreationProgress onLaunchMoreAds={handleLaunchMoreAds} />
              </Box>
            ) : (
              <Suspense fallback={<CircularProgress />}>
                {isJobStatusFinished ? (
                  <JobResultTable rows={jobStatusResponse?.result} />
                ) : (
                  <StepComponent />
                )}
              </Suspense>
            )}
          </Box>
        </Box>
        <FormNavigation
          isJobStatusFinished={isJobStatusFinished}
          onRestart={handleRestart}
          isDisabledRestart={!currentStep || isNavigationLocked}
          onBack={handlePrevStep}
          isDisabledBack={!currentStep || isNavigationLocked}
          onNext={handleNextStep}
          isDisabledNext={isDisabledNext}
          isLoadingNext={isCreateAdsPending}
          titleNext={
            currentStep === initialSteps.length - 1 ? 'Create Ads' : 'Next'
          }
          childrenOnTheRightSide={
            <>
              <>
                <AdSetAdsCounter />
                <AdSetBudgetCounter />
              </>
              {currentStep === 0 && isMarketingProcessHypothesisTesting ? (
                <>
                  <SplitInPacksButton />
                  <CreateMixedPackButton />
                  <AddTestingPackButton />
                </>
              ) : null}
              {currentStep === 0 && !isMarketingProcessHypothesisTesting && !isMarketingProcessScalingEg ? (
                <AddPackButton />
              ) : null}
            </>
          }
        />
      </Box>
      </StepperPaper>
      {!jobStatusResponse?.result && (
        <ConfirmationDialog
          isUnblock={isCreateAdsSuccess && isJobStatusFinished}
        />
      )}
    </FormProvider>
  );
};
export default FacebookForm;
